import { useCanViewIdentificationStats } from '@app/hooks/useCanViewIdentificationStats';
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { Button, Icon, LinkTo, Title } from '@common/components';
import { SidebarSection } from '@app/components/SidebarSection';
import {
  faClock,
  faClose,
  faFileCircleCheck,
  faMagnifyingGlassChart,
  faBuildingFlag,
  faList,
  faScaleBalanced,
  faLock,
  faEnvelopeOpen,
  faCookieBite,
  faTableCells,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { Permission } from '@app/components/Permission';
import { PERMISSIONS } from '@app/constants/permissions';

import { PRIVACY } from '@features/privacy/routes';
import { LEGAL } from '@features/legal/routes';
import menuSidebarClasses from './MenuSidebar.module.scss';

export default function MenuSidebar({ isOpen, setIsOpen }) {
  const { t } = useTranslation('app\\MenuSidebar', { useSuspense: false });

  const canViewIdentificationStats = useCanViewIdentificationStats();

  return (
    <div className={menuSidebarClasses.menuSidebar}>
      <div className={menuSidebarClasses.top}>
        <div className={`${menuSidebarClasses.containers}`}>
          <div className={menuSidebarClasses.header}>
            <Link to="/">
              <img
                alt="datalab-logo"
                src="/img/datalab-logo-neg.png"
                width={135}
                className={menuSidebarClasses.logo}
              />
            </Link>
            <Button
              className={menuSidebarClasses.closeButton}
              variant="flat"
              onClick={() => setIsOpen(!isOpen)}
            >
              <Icon color="white" icon={faClose} size="2x" />
            </Button>
          </div>
          <div className={`${menuSidebarClasses.containers}`}>
            <SidebarSection title={t('sidebarSectionStats')}>
              <LinkTo
                href="/dashboard/data/usage"
                onClick={() => setIsOpen(false)}
                className={menuSidebarClasses.link}
                color="primary"
                pointer
                prefix={
                  <Icon
                    className={menuSidebarClasses.menuIcon}
                    color="grey-300"
                    icon={faClock}
                  />
                }
              >
                {t('optionUsage')}
              </LinkTo>

              <LinkTo
                href="/dashboard/data/results"
                onClick={() => setIsOpen(false)}
                className={`${menuSidebarClasses.link}`}
                color="primary"
                pointer
                prefix={
                  <Icon
                    className={menuSidebarClasses.menuIcon}
                    color="grey-300"
                    icon={faFileCircleCheck}
                  />
                }
              >
                {t('optionResults')}
              </LinkTo>
            </SidebarSection>
          </div>
          <SidebarSection title={t('sidebarSectionNeuro')}>
            <LinkTo
              href="/dashboard/neuro/analysis"
              onClick={() => setIsOpen(false)}
              className={`${menuSidebarClasses.link}`}
              color="primary"
              pointer
              prefix={
                <Icon
                  className={menuSidebarClasses.menuIcon}
                  color="grey-300"
                  icon={faMagnifyingGlassChart}
                />
              }
            >
              {t('optionAnalysis')}
            </LinkTo>
            <LinkTo
              href="/dashboard/neuro/identification"
              onClick={() => setIsOpen(false)}
              className={`${menuSidebarClasses.link} ${
                !canViewIdentificationStats ? 'disabled' : ''
              }`}
              color="primary"
              pointer
              prefix={
                <Icon
                  className={menuSidebarClasses.menuIcon}
                  color="grey-300"
                  icon={faMagnifyingGlassChart}
                />
              }
            >
              {t('optionIdentify')}
            </LinkTo>
          </SidebarSection>
        </div>

        <div className={`${menuSidebarClasses.containers}`}>
          <div className={`${menuSidebarClasses.centers}`}>
            <SidebarSection title={t('sidebarSectionCenters')}>
              <LinkTo
                href="/dashboard/centers"
                onClick={() => setIsOpen(false)}
                className={menuSidebarClasses.link}
                color="primary"
                pointer
                prefix={
                  <Icon
                    className={menuSidebarClasses.menuIcon}
                    color="grey-300"
                    icon={faBuildingFlag}
                  />
                }
              >
                {t('optionList')}
              </LinkTo>
              <Permission permission={PERMISSIONS.VIEW.syncMonitor}>
                <LinkTo
                  href="/dashboard/sync-monitor"
                  onClick={() => setIsOpen(false)}
                  className={menuSidebarClasses.link}
                  color="primary"
                  pointer
                  prefix={
                    <Icon
                      className={menuSidebarClasses.menuIcon}
                      color="grey-300"
                      icon={faTableCells}
                    />
                  }
                >
                  {t('syncMonitor')}
                </LinkTo>
              </Permission>
            </SidebarSection>
          </div>
          <Permission permission={PERMISSIONS.VIEW.usersList}>
            <div className={`${menuSidebarClasses.users}`}>
              <SidebarSection title={t('sidebarSectionUsers')}>
                <LinkTo
                  href="/dashboard/users"
                  onClick={() => setIsOpen(!isOpen)}
                  className={`${menuSidebarClasses.link}`}
                  color="primary"
                  pointer
                  prefix={
                    <Icon
                      className={menuSidebarClasses.menuIcon}
                      color="grey-300"
                      icon={faList}
                    />
                  }
                >
                  {t('optionList')}
                </LinkTo>
              </SidebarSection>
            </div>
          </Permission>
        </div>
      </div>
      <div className={menuSidebarClasses.bottom}>
        <div className={menuSidebarClasses.corporate}>
          <SidebarSection title={t('sidebarSectionEnterprise')}>
            <LinkTo
              href={LEGAL}
              onClick={() => setIsOpen(!isOpen)}
              className={menuSidebarClasses.link}
              color="primary"
              pointer
              prefix={
                <Icon
                  className={menuSidebarClasses.menuIcon}
                  color="grey-300"
                  icon={faScaleBalanced}
                />
              }
            >
              {t('linkLegal')}
            </LinkTo>
            <LinkTo
              href={PRIVACY}
              onClick={() => setIsOpen(!isOpen)}
              className={menuSidebarClasses.link}
              color="primary"
              pointer
              prefix={
                <Icon
                  className={menuSidebarClasses.menuIcon}
                  color="grey-300"
                  icon={faLock}
                />
              }
            >
              {t('linkPrivacy')}
            </LinkTo>
            <LinkTo
              href="/info/cookies"
              onClick={() => setIsOpen(!isOpen)}
              className={menuSidebarClasses.link}
              color="primary"
              pointer
              prefix={
                <Icon
                  className={menuSidebarClasses.menuIcon}
                  color="grey-300"
                  icon={faCookieBite}
                />
              }
            >
              {t('linkCookies')}
            </LinkTo>
            <LinkTo
              href="/support"
              onClick={() => setIsOpen(!isOpen)}
              className={`${menuSidebarClasses.link}`}
              color="primary"
              pointer
              prefix={
                <Icon
                  className={menuSidebarClasses.menuIcon}
                  color="grey-300"
                  icon={faEnvelopeOpen}
                />
              }
            >
              {t('linkSupport')}
            </LinkTo>
          </SidebarSection>
        </div>
        <div className={menuSidebarClasses.footer}>
          <Link to="/">
            <img
              alt="itenlearning-logo"
              src="/img/itenlearning-logo-neg.png"
              width={125}
              className={menuSidebarClasses.logo}
            />
          </Link>
          <Title
            type="span"
            color="white"
            className={menuSidebarClasses.copyright}
          >
            {t('copyright')}
          </Title>
        </div>
      </div>
    </div>
  );
}
MenuSidebar.propTypes = {
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func,
};
